import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { cn } from "@utils/tw"
import { defineBlock, EditableText, useInlineEditableValue } from "eddev/blocks"
import { Fragment, useEffect, useRef, useState } from "react"
import { motion } from "motion/react"
import { Button } from "@components/atoms/Button"
import { Appear } from "@components/motion/Appear"

export const meta: BlockMeta = {
  title: "Features Tabs",
  tags: ["#page"],
}

export default defineBlock("content/features-tabs", (props) => {
  const features = props.features || []
  const [selected, setSelected] = useState(0)
  const [title, setTitle] = useInlineEditableValue("title", "")
  const showTitle = env.admin || (title && title.length > 0)

  return (
    <div className="content-sized my-blockSpace flex flex-col gap-6">
      {showTitle && (
        <Appear slide>
          <EditableText store="title" className="type-sans-2-xl-semibold" placeholder="Features Tabs Title" />
        </Appear>
      )}
      {env.admin && features.length === 0 && (
        <div className="w-full col-span-2 p-5 rounded-md bg-slate200 text-slate950 type-sans-base-regular">
          No features added. Please add features to display the features tabs.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="w-full flex flex-col gap-4">
          {props.features?.map((feature, index) => {
            const isSelected = index === selected
            const delay = index * 100

            return (
              !!feature && (
                <Fragment key={index}>
                  <Appear delay={delay} className={cn("w-full")}>
                    <div
                      className={cn(
                        "w-full p-5 rounded-md flex gap-4 justify-start items-center bg-slate400 text-slate950 cursor-pointer transition-all md:hover:bg-slate300",
                        isSelected && "bg-orange md:hover:bg-orange400",
                      )}
                      onClick={() => setSelected(index)}
                    >
                      <Appear
                        slide
                        delay={delay}
                        className="w-6 h-6 flex items-center justify-center rounded-full border border-current type-mono-xs-regular"
                      >
                        {index + 1}
                      </Appear>
                      <Appear
                        as="p"
                        slide
                        delay={delay + 100}
                        className="type-serif-base-regular md:type-serif-xl-regular"
                      >
                        {feature.title}
                      </Appear>
                    </div>
                  </Appear>
                  <div
                    className={cn(
                      "w-full md:hidden overflow-hidden transition-all duration-500 h-[95vw]",
                      !isSelected && "h-0 -mt-4",
                    )}
                  >
                    <Appear
                      delay={delay}
                      className={cn("w-full flex flex-col justify-between p-5 bg-slate200 rounded-md h-[95vw]")}
                    >
                      <p className="type-serif-lg-regular text-pretty">{feature.description}</p>
                      {feature.button && (
                        <Button
                          href={feature.button.url || undefined}
                          target={feature.button.target || undefined}
                          variant="dark"
                        >
                          {feature.button.title}
                        </Button>
                      )}
                    </Appear>
                  </div>
                </Fragment>
              )
            )
          })}
        </div>
        <div className="w-full self-stretch relative rounded-md bg-slate200 hidden md:block">
          {props.features?.map((feature, index) => {
            if (!feature?.description) return null
            const isSelected = index === selected
            const key = feature.description + index

            return (
              <div
                className={cn(
                  "w-full flex h-full flex-col justify-between absolute left-0 top-0 p-5 transition-opacity duration-300 opacity-0 pointer-events-none",
                  isSelected && "opacity-100 pointer-events-auto",
                )}
                key={key}
              >
                {isSelected && (
                  <>
                    <Appear
                      slide
                      as="p"
                      className={cn(
                        "type-serif-xl-regular text-pretty",
                        feature.smallerText && "type-serif-lg-regular",
                      )}
                    >
                      {feature.description}
                    </Appear>
                    {feature.button && (
                      <Appear delay={150}>
                        <Button
                          href={feature.button.url || undefined}
                          target={feature.button.target || undefined}
                          variant="dark"
                        >
                          {feature.button.title}
                        </Button>
                      </Appear>
                    )}
                  </>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
})
